/**
 * SensorPodBridge — BrightSign peripheral bridge for external CSI sensor pods.
 *
 * Deployment shape (see docs/peripherals.md and docs/uvc-spike.md):
 *
 *   BrightSign players have no UVC camera path — UVC is not exposed through
 *   BrightScript, the Chromium build has camera permissions disabled, and
 *   the Node.js runtime does not expose `/dev/video0`. The Wave 2B Edge AI
 *   hook therefore uses an external sensor pod (ESP32 or Raspberry Pi Zero W)
 *   running the existing Trillboards CSI pipeline. The pod posts UDP datagrams
 *   to the BrightSign player's LAN IP on port 5555 every ~10s. Each datagram
 *   is a JSON line in the CsiSnapshot shape.
 *
 *   `sensor-pod-listener.brs` receives those datagrams, parses them, rate
 *   limits, and forwards to this bridge via
 *     BSMessagePort.onbsmessage({ type: 'sensor-pod-signal', ... })
 *
 *   This bridge listens for sensor-pod-signal messages, translates each one
 *   into the canonical `audienceSignals` socket payload, and emits it via
 *   `window.trillboardsSocket.emit('audienceSignals', payload)`. The backend
 *   `socket.js` `audienceSignals` handler treats the result as a CSI-backed
 *   observation (`measurementQuality='csi_pod'`, `evidenceGrade='csi_backed'`)
 *   with the same validation / rate-limit / FEIN enrichment path.
 *
 * Only active when `window.BSDeviceInfo` is present AND the remote feature
 * flag `window.__TRILLBOARDS_BRIGHTSIGN_PERIPHERALS_DISABLED !== true`.
 *
 * ── ZERO PODS ARE FIELDED. THIS BRIDGE HAS NEVER PRODUCED A ROW. ───────────
 * Measured 2026-08-15 across the FULL retention window of
 * `trillboards.audience_metrics_dist` (2026-05-17 → now), grouped by
 * `evidence_grade`:
 *
 *   face_backed   37,297,323      person_backed    772,470
 *   empty            165,308      speech_only        9,488
 *   proxy_only         4,091      csi_backed             0
 *
 * `csi_backed` is not rare here — it is absent, and has been since the grade
 * existed. This bridge is the only producer of it anywhere in the tree, and it
 * only runs on a BrightSign player that has an ESP32/Pi-Zero pod physically
 * installed on the same LAN. No such pod exists in the field.
 *
 * So do not read the software as broken, and do not go looking for a bug in
 * the grading ladder. A soak once watched for the csi/face ratio to cross 0.10
 * and the numerator never left zero. The gate is hardware installation — the
 * one class of work an agent genuinely cannot do — so there is nothing to
 * track and nothing waiting on code, and THIS COMMENT is where the fact lives.
 * The query below is the check: it answers on live data whenever anyone asks,
 * which is strictly better than a row asserting the same thing from a date in
 * the past.
 *
 * BEFORE TREATING csi_backed AS AVAILABLE ANYWHERE — a report, a floor, a
 * curated deal, a coverage claim to a buyer — re-run the group-by above. The
 * published methodology at `routes/partner/methodologyDocument.js` lists
 * csi_backed among `decisionable_grades` and describes its rollout as "paced
 * by hardware installation", which is accurate; it is NOT a statement that any
 * pod is live.
 */

/* eslint-disable no-console */

const BRIDGE_NAME = 'SensorPodBridge';

// Debounce + rate-limit state. The BrightScript side already rate-limits
// outbound datagrams to at most 1/5s but we defend the socket layer
// independently. Backend also rate-limits via audienceSignalsRateLimit.
const MIN_EMIT_INTERVAL_MS = 5000;

// Minimum signal quality we consider "decisionable". Below this the snapshot
// is still forwarded for network-aggregate counters but with decisionability
// set to non_decisionable / reasons = ['csi_low_confidence'].
const MIN_DECISIONABLE_SIGNAL_QUALITY = 0.4;

// Motion-to-attention mapping constants. A fully-still frame (motionScore=0)
// produces avgAttention=0; a busy frame (motionScore>=0.6) saturates at 1.0.
// This mirrors how AudienceSensingService.kt derives attention from body
// engagement when a camera is unavailable.
const MOTION_ATTENTION_CAP = 0.6;

/**
 * Idempotent install flag — ensures init() can be called twice without
 * double-subscribing to BSMessagePort.
 */
let _installed = false;

/**
 * Last emission timestamp (ms) for rate limiting.
 *
 * Sentinel: Number.NEGATIVE_INFINITY means "never emitted", so the first
 * call always passes the rate-limit guard. Each successful emit overwrites
 * this with the current nowMs.
 */
let _lastEmitMs = Number.NEGATIVE_INFINITY;

/**
 * Counters for tests / health checks.
 */
const _counters = {
  received: 0,
  emitted: 0,
  droppedInvalid: 0,
  droppedRateLimited: 0,
  droppedNoSocket: 0,
};

/**
 * Coerce a possibly-invalid number into a clamped finite integer.
 */
function coerceInt(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  const rounded = Math.round(n);
  if (typeof min === 'number' && rounded < min) return min;
  if (typeof max === 'number' && rounded > max) return max;
  return rounded;
}

/**
 * Coerce a possibly-invalid number into a clamped finite float.
 */
function coerceFloat(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (typeof min === 'number' && n < min) return min;
  if (typeof max === 'number' && n > max) return max;
  return n;
}

/**
 * Parse a BSMessagePort message. BrightSign can deliver messages as either
 * an associative array (already deserialized) or a JSON string — handle both.
 */
function parseIncomingMessage(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }
  return null;
}

/**
 * Validate that a parsed sensor-pod message has the minimum required fields.
 * Returns true if the message can be safely forwarded.
 */
function isValidSensorPodSignal(msg) {
  if (!msg || typeof msg !== 'object') return false;
  if (msg.type !== 'sensor-pod-signal') return false;
  if (typeof msg.occupantCount !== 'number' && Number.isNaN(Number(msg.occupantCount))) {
    return false;
  }
  const occ = Number(msg.occupantCount);
  if (!Number.isFinite(occ) || occ < 0) return false;
  return true;
}

/**
 * Translate a CSI snapshot into the canonical `audienceSignals` payload.
 *
 * The backend's socket.js handler validates the following fields via
 * validateSignalsPayload():
 *   - adReceptivityScore (0..1, default 0.5 on invalid)
 *   - avgAttention       (0..1, default 0 on invalid)
 *   - avgFaceCount       (0..100, default 0 on invalid)
 *   - maxFaceCount       (0..100, default 0 on invalid)
 *   - avgNoiseLevel      (0..120, optional)
 *   - crowdedPct         (0..1, optional)
 *
 * CSI sensor pods produce occupantCount (derived from WiFi amplitude +
 * phase estimation) which we map onto avgFaceCount/maxFaceCount directly.
 * motionScore maps to avgAttention via a saturating ramp.
 */
function buildAudienceSignalsPayload(msg, context) {
  const { fingerprint, screenId } = context;
  const occupantCount = coerceInt(msg.occupantCount, 0, 0, 100);
  const motionScore = coerceFloat(msg.motionScore, 0, 0, 1);
  const signalQuality = coerceFloat(msg.signalQuality, 0, 0, 1);
  const avgAttention = Math.min(1, motionScore / MOTION_ATTENTION_CAP);

  // Decisionability: only flag decisionable if we have occupants AND the
  // signal quality is above the minimum. Otherwise downstream intelligence
  // queues should skip this observation (matches WebSensingBridge logic).
  const isDecisionable =
    occupantCount > 0 && signalQuality >= MIN_DECISIONABLE_SIGNAL_QUALITY;
  const decisionBlockReasons = [];
  if (occupantCount === 0) decisionBlockReasons.push('no_occupants_detected');
  if (signalQuality < MIN_DECISIONABLE_SIGNAL_QUALITY) {
    decisionBlockReasons.push('csi_low_confidence');
  }

  return {
    fingerprint,
    screenId,
    // Face counts are zero because the sensor pod has no camera; surface
    // occupantCount as a first-class field so FEIN / dashboards can use it.
    avgFaceCount: occupantCount,
    maxFaceCount: occupantCount,
    occupantCount,
    avgAttention,
    adReceptivityScore: isDecisionable ? Math.min(1, signalQuality) : 0.5,
    // Optional metadata the backend already accepts.
    signalQuality,
    motionScore,
    framesProcessed: coerceInt(msg.framesProcessed, 0, 0, 1e9),
    framesDropped: coerceInt(msg.framesDropped, 0, 0, 1e9),
    windowStart: coerceInt(msg.windowStart, Date.now(), 0, Number.MAX_SAFE_INTEGER),
    windowEnd: coerceInt(msg.windowEnd, Date.now(), 0, Number.MAX_SAFE_INTEGER),
    timestamp: coerceInt(msg.timestamp, Date.now(), 0, Number.MAX_SAFE_INTEGER),
    // Source attribution — backend can branch on these for CSI-specific
    // enrichment and anomaly detection.
    source: 'csi-pod',
    ingestSource: 'brightsign_sensor_pod',
    sensingIntervalMs: 10000,
    avgDwellTimeMs: 0,
    measurementQuality: 'csi_pod',
    observationFamily:
      occupantCount > 0 ? 'screen_audience' : 'empty_scene',
    evidenceGrade: occupantCount > 0 ? 'csi_backed' : 'empty',
    decisionability: isDecisionable ? 'decisionable' : 'non_decisionable',
    decisionBlockReasons,
    ...(typeof msg.breathingRateBpm === 'number'
      ? { breathingRateBpm: msg.breathingRateBpm }
      : {}),
    ...(typeof msg.heartRateBpm === 'number'
      ? { heartRateBpm: msg.heartRateBpm }
      : {}),
    ...(typeof msg.vitalConfidence === 'number'
      ? { vitalConfidence: msg.vitalConfidence }
      : {}),
  };
}

/**
 * Resolve the screen fingerprint from localStorage. Bootstrap sets this
 * from BSDeviceInfo.serialNumber via the `brightsign-<serial>` convention.
 */
function resolveFingerprint() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem('trillboard_fingerprint') || null;
    }
  } catch (_) {
    // private browsing / quota
  }
  return null;
}

/**
 * Resolve the globally-installed socket instance. overlay/main.js sets this
 * when the screen pairs. Returns null when the socket isn't ready yet.
 */
function resolveSocket() {
  if (typeof window === 'undefined') return null;
  return window.trillboardsSocket || null;
}

/**
 * Resolve the current screen id from the socket. The socket is annotated
 * with screenId after the backend binds the device; before pairing this is
 * null and the backend defers the screenId lookup via fingerprint.
 */
function resolveScreenId() {
  const sock = resolveSocket();
  if (sock && sock.screenId) return sock.screenId;
  if (
    typeof window !== 'undefined' &&
    window.__TRILLBOARD_CONFIG__ &&
    window.__TRILLBOARD_CONFIG__.screenId
  ) {
    return window.__TRILLBOARD_CONFIG__.screenId;
  }
  return null;
}

/**
 * Handle one sensor-pod-signal message. Validates, builds the canonical
 * payload, rate-limits, and emits via the socket. Returns one of:
 *   'emitted'             — payload sent to the backend
 *   'dropped_invalid'     — message failed schema validation
 *   'dropped_rate_limit'  — within MIN_EMIT_INTERVAL_MS of previous emit
 *   'dropped_no_socket'   — no socket, nothing to emit to
 */
function handleSensorPodSignal(rawMessage, { nowMs } = {}) {
  _counters.received += 1;
  const msg = parseIncomingMessage(rawMessage);
  if (!isValidSensorPodSignal(msg)) {
    _counters.droppedInvalid += 1;
    return 'dropped_invalid';
  }

  const fingerprint = resolveFingerprint();
  if (!fingerprint) {
    // Without a fingerprint we cannot authenticate the socket write. Drop
    // rather than emit an auth-reject payload.
    _counters.droppedNoSocket += 1;
    return 'dropped_no_socket';
  }

  const socket = resolveSocket();
  if (!socket || typeof socket.emit !== 'function') {
    _counters.droppedNoSocket += 1;
    return 'dropped_no_socket';
  }

  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  if (now - _lastEmitMs < MIN_EMIT_INTERVAL_MS) {
    _counters.droppedRateLimited += 1;
    return 'dropped_rate_limit';
  }

  const screenId = resolveScreenId();
  const payload = buildAudienceSignalsPayload(msg, { fingerprint, screenId });

  try {
    socket.emit('audienceSignals', payload);
    _counters.emitted += 1;
    _lastEmitMs = now;
    return 'emitted';
  } catch (err) {
    // Socket.emit can throw when the socket is mid-disconnect. Count as
    // rate-limited (do NOT advance _lastEmitMs so the next message retries).
    _counters.droppedNoSocket += 1;
    return 'dropped_no_socket';
  }
}

/**
 * Install the BSMessagePort subscriber. Idempotent — repeat calls only log
 * a warning and return the already-installed handler.
 *
 * The bridge is gated behind TWO signals:
 *   - window.BSDeviceInfo must be present (we only run on BrightSign)
 *   - window.__TRILLBOARDS_BRIGHTSIGN_PERIPHERALS_DISABLED must not be true
 *     (feature flag knob for emergencies / remote disable)
 */
function init() {
  if (typeof window === 'undefined') return false;
  if (_installed) return true;

  // Feature detect BrightSign. If BSDeviceInfo is missing we are running
  // in a browser / Android and the bridge must be a no-op. This also
  // protects tests that import the file without the shim active.
  if (typeof window.BSDeviceInfo === 'undefined') return false;

  // Feature flag — can be flipped by the portal (via __TRILLBOARD_CONFIG__)
  // or by bootstrap query param to kill the bridge remotely without a
  // redeploy. Disabled=true means the BrightSign player keeps running the
  // rest of the screen bundle but ignores sensor-pod traffic entirely.
  if (window.__TRILLBOARDS_BRIGHTSIGN_PERIPHERALS_DISABLED === true) {
    console.log(
      '[' + BRIDGE_NAME + '] disabled by flag; skipping install'
    );
    return false;
  }

  const port = window.BSMessagePort;
  if (!port) return false;

  const handler = (rawMessage) => {
    try {
      // BSMessagePort events may arrive as a CustomEvent ({ detail: msg })
      // in the dev shim, or as the bare message on real hardware. Unwrap
      // when we see the CustomEvent shape.
      let data = rawMessage;
      if (rawMessage && typeof rawMessage === 'object' && 'detail' in rawMessage) {
        data = rawMessage.detail && rawMessage.detail.message
          ? rawMessage.detail.message
          : rawMessage.detail;
      }
      handleSensorPodSignal(data);
    } catch (err) {
      // Never throw out of a BSMessagePort handler — BrightSign's event
      // loop will not recover. Log and continue.
      try {
        console.error('[' + BRIDGE_NAME + '] handler error', err);
      } catch (_) { /* noop */ }
    }
  };

  if (typeof port.addEventListener === 'function') {
    port.addEventListener('bsmessage', handler);
  } else {
    // Legacy real-hardware pattern — assign to onbsmessage. Preserve any
    // existing handler so we compose rather than overwrite (GpioCounterBridge
    // may also attach to the same port).
    const prior = port.onbsmessage;
    port.onbsmessage = function (msg) {
      if (typeof prior === 'function') {
        try {
          prior(msg);
        } catch (err) {
          try {
            console.error('[' + BRIDGE_NAME + '] prior handler error', err);
          } catch (_) { /* noop */ }
        }
      }
      handler(msg);
    };
  }

  _installed = true;
  try {
    window.__TRILL_BS_SENSOR_POD_BRIDGE__ = true;
    console.log('[' + BRIDGE_NAME + '] installed');
  } catch (_) {
    /* ignore */
  }
  return true;
}

/**
 * Test-only helper — tears down the bridge between test cases.
 */
function __resetForTests() {
  _installed = false;
  _lastEmitMs = Number.NEGATIVE_INFINITY;
  _counters.received = 0;
  _counters.emitted = 0;
  _counters.droppedInvalid = 0;
  _counters.droppedRateLimited = 0;
  _counters.droppedNoSocket = 0;
  if (typeof window !== 'undefined') {
    try {
      delete window.__TRILL_BS_SENSOR_POD_BRIDGE__;
    } catch (_) {
      window.__TRILL_BS_SENSOR_POD_BRIDGE__ = undefined;
    }
  }
}

const __getCountersForTests = () => ({ ..._counters });

export {
  init,
  handleSensorPodSignal,
  buildAudienceSignalsPayload,
  isValidSensorPodSignal,
  parseIncomingMessage,
  coerceInt,
  coerceFloat,
  __resetForTests,
  __getCountersForTests,
  MIN_EMIT_INTERVAL_MS,
  MIN_DECISIONABLE_SIGNAL_QUALITY,
  MOTION_ATTENTION_CAP,
  BRIDGE_NAME,
};
